import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES, setLanguage } from '../i18n';
import { useLanguage } from '../context/LanguageContext';

/**
 * Language switcher dropdown. Lightweight, no external UI lib.
 *
 * Props:
 *  - variant: 'dark' (white text, transparent bg) | 'light' (default — slate text, white bg)
 *  - compact: when true, shows only the flag (good for tight navbars)
 *
 * Bridges two i18n systems:
 *  1. react-i18next (new) — drives every component using `t()` + JSON locales
 *     (all 14 languages).
 *  2. LanguageContext (legacy) — only supports `en` / `es`; used by a few
 *     marketing landing components. We keep it in sync so picking Spanish
 *     in the new switcher also translates those legacy surfaces, and any
 *     other choice falls back to `en` (graceful default).
 */
const LanguageSwitcher = ({ variant = 'light', compact = false }) => {
  const { i18n } = useTranslation();
  const legacy = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const change = async (code) => {
    // Load the translations chunk BEFORE switching i18n's active language —
    // otherwise React re-renders with the new code while the bundle is
    // still in-flight, causing a "flash of English".
    await setLanguage(code);
    // Keep the legacy LanguageContext in sync — only knows en/es.
    if (legacy?.setLang) legacy.setLang(code === 'es' ? 'es' : 'en');
    try {
      localStorage.setItem('nofee_lang', code);
      // Mark that the user manually picked a language — so PublicMenu won't auto-override it
      localStorage.setItem('nofee_lang_manual', '1');
    } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const triggerCls =
    variant === 'dark'
      ? 'text-white/80 hover:text-white border-white/20 hover:border-white/40 bg-white/5'
      : 'text-slate-700 hover:text-slate-900 border-slate-200 hover:border-slate-300 bg-white';

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border-2 text-sm font-semibold transition-all ${triggerCls}`}
        data-testid="language-switcher-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="w-4 h-4" />
        <span className="text-base leading-none">{current.flag}</span>
        {!compact && <span className="hidden sm:inline">{current.native}</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-56 max-h-[420px] overflow-y-auto rounded-xl bg-white border border-slate-200 shadow-2xl shadow-black/10 z-50 py-1"
          role="listbox"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => change(lang.code)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors ${
                  active ? 'bg-gold-50 text-gold-700 font-bold' : 'text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`language-option-${lang.code}`}
                role="option"
                aria-selected={active}
              >
                <span className="text-lg leading-none">{lang.flag}</span>
                <span className="flex-1">{lang.native}</span>
                {active && <Check className="w-4 h-4 text-gold-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
